import dayjs from "dayjs";
import {CartTimeline} from "@/data/entities/cartTimeline";

/** Query key prefix of the cart timeline, to refresh it. */
export const CART_TIMELINE_QUERY_KEY = "cart-timeline";

/** How often the timeline of today refreshes by itself. */
export const CART_TIMELINE_REFRESH_INTERVAL = 30_000;

/**
 * What happened to a cart, derived from its fields:
 * - `active`: a customer is filling it right now
 * - `paying`: the customer is paying at Mollie
 * - `ordered`: an order was placed from it on the ordering site
 * - `pos`: an order placed via the POS (processed right away)
 * - `expired`: not ordered within the expiration time
 * - `reset`: emptied by resetting, or replaced after other order settings
 */
export type CartKind = "active" | "paying" | "ordered" | "pos" | "expired" | "reset";

/** Filter of the timeline and the table. */
export type CartKindFilter = "all" | "active" | "ordered" | "expired" | "reset";

export const cartKindFilters: CartKindFilter[] = ["all", "active", "ordered", "expired", "reset"];

/**
 * A POS order also makes a cart, processed right away. Recognized by the
 * source of its order.
 */
export const isPosCart = (cart: CartTimeline): boolean => cart.order?.source === "POS";

export const cartKind = (cart: CartTimeline, now: Date): CartKind => {
    if (isPosCart(cart))
        return "pos";
    if (cart.status === "PROCESSED")
        return "ordered";
    if (cart.resetOn !== null)
        return "reset";
    if (!dayjs(cart.expiresOn).isAfter(now))
        return "expired";
    return cart.status === "WAITING_FOR_PAYMENT" ? "paying" : "active";
};

/** Whether a customer may still be busy with the cart: filling it or paying. */
export const isActiveKind = (kind: CartKind): boolean => kind === "active" || kind === "paying";

export const matchesFilter = (kind: CartKind, filter: CartKindFilter): boolean => {
    switch (filter) {
        case "all":
            return true;
        case "active":
            return isActiveKind(kind);
        case "ordered":
            return kind === "ordered" || kind === "pos";
        default:
            return kind === filter;
    }
};

/**
 * Where the bar of a cart ends: when it was ordered, reset, or expired, or
 * now while it's still active. An ordered cart without its order ends when it
 * would have expired.
 */
export const cartEnd = (cart: CartTimeline, kind: CartKind, now: Date): Date => {
    switch (kind) {
        case "pos":
            return new Date(cart.creationDate);
        case "ordered":
            return new Date(cart.order?.orderedOn ?? cart.expiresOn);
        case "reset":
            return new Date(cart.resetOn!);
        case "active":
        case "paying":
            return now;
        default:
            return new Date(cart.expiresOn);
    }
};

/** Time of day, e.g. `18:45`. */
export const formatTime = (date: string | Date): string => dayjs(date).format("HH:mm");

/** Minutes since the start of the day of `day`, e.g. 18:45 -> 1125. */
export const minutesOfDay = (date: Date, day: Date): number =>
    dayjs(date).diff(dayjs(day).startOf("day"), "minute", true);

/** Duration in whole minutes (at least 0). */
export const durationMinutes = (from: Date, until: Date): number =>
    Math.max(0, Math.round(dayjs(until).diff(dayjs(from), "minute", true)));

/** A cart with everything the timeline and table show. */
export type CartInTimeline = {
    cart: CartTimeline,
    kind: CartKind,
    start: Date,
    end: Date,
};

export const toCartsInTimeline = (carts: CartTimeline[], now: Date): CartInTimeline[] =>
    carts.map((cart: CartTimeline) => {
        const kind: CartKind = cartKind(cart, now);
        return {
            cart,
            kind,
            start: new Date(cart.creationDate),
            end: cartEnd(cart, kind, now),
        };
    });

/**
 * Puts the carts in rows (lanes), so bars that overlap in time are shown
 * under each other. Each cart goes in the first lane that is free again.
 */
export const toLanes = (carts: CartInTimeline[], minGapMinutes = 3, minBarMinutes = 6): CartInTimeline[][] => {
    const sorted = [...carts].sort((a, b) => a.start.getTime() - b.start.getTime() || a.cart.id - b.cart.id);
    const laneEnds: number[] = [];
    const lanes: CartInTimeline[][] = [];

    sorted.forEach((c: CartInTimeline) => {
        const start: number = c.start.getTime();
        const visualEnd: number = Math.max(c.end.getTime(), start + minBarMinutes * 60_000);
        let lane: number = laneEnds.findIndex(end => end + minGapMinutes * 60_000 <= start);
        if (lane === -1) {
            lane = laneEnds.length;
            lanes.push([]);
        }
        laneEnds[lane] = visualEnd;
        lanes[lane].push(c);
    });

    return lanes;
};

/**
 * Hours shown on the axis: at least 11:00 to 23:00, wider when carts fall
 * outside of that.
 */
export const axisHours = (carts: CartInTimeline[], day: Date): { from: number, to: number } => {
    let from = 11;
    let to = 23;
    carts.forEach((c: CartInTimeline) => {
        from = Math.min(from, Math.floor(minutesOfDay(c.start, day) / 60));
        to = Math.max(to, Math.ceil(minutesOfDay(c.end, day) / 60));
    });

    return {from: Math.max(0, from), to: Math.min(24, to)};
};